<template>
  <div class="login-container">
    <div class="login-header">
      <div class="logo-section">
        <img
          src="@/assets/images/experian-logo.png"
          alt="Company Logo"
          class="company-logo"
        />
      </div>
      <p class="login-subtitle">Sign in to your account to continue</p>
    </div>

    <form
      @submit.prevent="onSubmit"
      class="login-form"
    >
      <div>
        <label for="username">Username</label>
        <input
          id="username"
          v-model="username"
          type="text"
          required
        />
      </div>
      <div>
        <label for="password">Password</label>
        <input
          id="password"
          v-model="password"
          type="password"
          required
        />
      </div>
      <button
        type="submit"
        :disabled="isLoading"
      >
        {{ isLoading ? 'Logging in...' : 'Login' }}
      </button>
      <div
        v-if="error"
        class="error"
      >
        {{ error }}
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
  import { nextTick, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useFeatureStore } from '@/app/shared/stores/feature-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const router = useRouter();
  const authStore = useAuthStore();
  const alertStore = useAlertStore();

  const username = ref('');
  const password = ref('');
  const error = ref('');
  const isLoading = ref(false);

  async function onSubmit() {
    if (!username.value || !password.value) {
      error.value = 'Please enter both username and password.';
      return;
    }

    try {
      isLoading.value = true;
      error.value = '';

      await authStore.loginWithForm(username.value, password.value);

      alertStore.showNotification(
        'Login successful',
        'Authentication',
        Motif.SUCCESS
      );
      await nextTick();
      const redirect = router.currentRoute.value.query.redirect as string;
      router.push(redirect || { name: 'base-home' });
    } catch (err: any) {
      error.value = err.message || t('loginFail');
    } finally {
      isLoading.value = false;
    }
  }
</script>

<style scoped>
  .login-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    padding: 2rem;
  }

  .login-header {
    text-align: center;
    margin-bottom: 1rem;
    color: #2d3748;
  }

  .logo-section {
    margin-bottom: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .company-logo {
    height: 60px;
  }

  .login-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .login-subtitle {
    font-size: 1.1rem;
    opacity: 0.95;
    font-weight: 300;
  }

  .login-form {
    max-width: 400px;
    width: 100%;
    padding: 2.5rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .login-form div {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .login-form label {
    font-weight: 600;
    color: #2d3748;
    font-size: 0.9rem;
  }

  .login-form input {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s;
  }

  .login-form input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  .login-form button {
    padding: 0.875rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    transition: all 0.3s;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    margin-top: 0.5rem;
  }

  .login-form button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }

  .login-form button:active:not(:disabled) {
    transform: translateY(0);
  }

  .login-form button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .error {
    color: #c53030;
    font-size: 0.875rem;
    background: #fff5f5;
    padding: 0.75rem;
    border-radius: 6px;
    border: 1px solid #feb2b2;
  }

  @media (max-width: 640px) {
    .login-container {
      padding: 1rem;
    }

    .login-title {
      font-size: 2rem;
    }

    .login-subtitle {
      font-size: 1rem;
    }

    .login-form {
      padding: 1.5rem;
    }
  }
</style>
